var uaParser = new (require('ua-parser-js'))();
const _ = require('underscore');
const model = require('model/model');
const session = require('core/session');
const { closeGCAIframe } = require('utilities');

const experimentUtilities = require('utilities/experimentUtilities');
const experimentKeys = Object.keys(experimentUtilities.names).reduce((acc, k) => {
	let value = experimentUtilities.names[k];
	acc[value] = true;
	return acc;
}, {});

const TOKEN_TIMEOUT = 1000 * 60 * 60 * 12;

window.addEventListener('storage', function (event) {
	if (event && event.key === 'userContext') {
		let isIEorSafari = uaParser.getBrowser().name === 'IE' || uaParser.getBrowser().name === 'Safari';
		if (isIEorSafari && localStorage.userContext === event.newValue) {
			// Handle IE bug - storage event will be fired in source window
			return;
		}

		var oldValue = event.oldValue ? JSON.parse(event.oldValue) : {},
			newValue = event.newValue ? JSON.parse(event.newValue) : {};

		if (oldValue.token !== newValue.token && location.pathname !== '/pwr') {
			if (model.get('gcAddon')) {
				// Account swapped in another tab - close to prevent context errors
				closeGCAIframe();
				return;
			}

			var destination = '/';

			if (newValue.token) {
				if (newValue.classCode) {
					destination = '/student';
				}
			} else if (oldValue.token) {
				if (oldValue.classCode) {
					destination = '/student';
				}
			}

			location.href = destination;
		}
	}
});

module.exports = {
	/**
	 * Clear the context, effectively logging the session out.
	 *
	 * @return {Object} "this" for chaining.
	 */
	reset() {
		model.remove('userContext').remove('tokenExpiration').remove('sessionStore').remove('account');
		return this;
	},

	/**
	 * Initialize the context with information returned from a server login call.
	 *
	 * @param  {Object} context User information, including token, returned from server.
	 * @return {Object}         "this" for chaining.
	 */
	initialize(context, offline) {
		let oldContext = this.getUserData();
		if (!context) {
			throw new Error('Attempted to initialize empty context');
		}

		// Already logged in
		// most likely switching from teacher to student or vice versa
		// OR students swapping accounts
		if (oldContext && oldContext.loggedIn) {
			session.initialize();
		}

		var tokenExpiration = Date.now() + TOKEN_TIMEOUT;

		context.loggedIn = true;

		model.set('userContext', context);
		model.set('sessionStore', {});

		if (!offline) {
			model.set('tokenExpiration', tokenExpiration);
		}

		// Remove unauthenticated session data in favor of user sessionStore
		model.remove('temporarySessionStore');
		model.remove('account');

		return this;
	},

	loggedIn() {
		return typeof model.get('userContext') !== 'undefined';
	},

	getUserData() {
		return model.get('userContext');
	},

	isStudent() {
		if (!this.loggedIn()) {
			return null;
		}

		var userContext = model.get('userContext');

		if (!userContext.classCode) {
			return false;
		}

		return true;
	},

	isTeacher() {
		if (!this.loggedIn()) {
			return null;
		}

		var userContext = model.get('userContext');

		if (userContext.classCode) {
			return false;
		}

		return true;
	},

	isExternal() {
		if (!this.loggedIn()) {
			return false;
		}

		return 'organization' in model.get('userContext');
	},

	getToken(token) {
		if (!this.loggedIn()) {
			return null;
		}

		return model.get('userContext').token;
	},

	setToken(token) {
		var userContext = model.get('userContext');

		if (userContext) {
			userContext.token = token;

			model.set('userContext', userContext);
		}
	},

	isVerified() {
		if (!this.loggedIn()) {
			return null;
		}

		var value = model.get('userContext').verified;

		if (value === false) {
			return false;
		} else {
			return true;
		}
	},

	getUsername() {
		if (!this.loggedIn()) {
			return null;
		}

		return model.get('userContext').username;
	},

	getStudentClassCode() {
		if (!this.loggedIn()) {
			return null;
		}

		var userContext = model.get('userContext');

		if (!userContext.classCode) {
			throw new Error('Cannot call `getStudentClassCode` for teacher!');
		}

		return userContext.classCode;
	},

	getStudentType() {
		if (!this.loggedIn()) {
			return null;
		}

		var userContext = model.get('userContext');

		if (!userContext.classCode) {
			throw new Error('Cannot call `getStudentType` for teacher!');
		}

		return userContext.type;
	},

	isFromDrupal() {
		if (!this.loggedIn()) {
			return null;
		}

		return model.get('userContext').fromDrupal;
	},

	getFirstName() {
		if (!this.loggedIn()) {
			return null;
		}

		return model.get('userContext').firstName;
	},

	getFullName() {
		if (!this.loggedIn()) {
			return null;
		}

		var userContext = model.get('userContext');

		return userContext.firstName + (userContext.lastName ? ' ' + userContext.lastName : '');
	},

	getELLFlag() {
		if (!this.loggedIn()) {
			return null;
		}

		var userContext = model.get('userContext');

		return userContext.ell;
	},

	/**
	 * Returns experiment group based on user id.
	 *
	 * @param  {Number} [n=2]	Number of groups in experiment
	 * @return {String}			Single-character experiment group ('A', 'B', etc.)
	 */
	getExperimentGroup(n) {
		if (!this.loggedIn()) {
			return null;
		}

		if (n === null || typeof n === 'undefined') {
			n = 2;
		}

		var uid = model.get('userContext').uid;

		// 65 is "A"
		// charCode + 1 to match quirk of previous implementation
		return String.fromCharCode(65 + ((uid.substring(uid.length - 1).charCodeAt(0) + 1) % n));
	},

	isExperimentParticipant(experiment) {
		if (!this.loggedIn()) {
			return null;
		}

		let userContext = model.get('userContext');
		let experiments = userContext.experiments || [];

		return experimentKeys[experiment] && experiments.indexOf(experiment) !== -1;
	},

	updateExperiments(experiments) {
		if (!this.loggedIn()) {
			return null;
		}

		let userContext = model.get('userContext');
		userContext = _.assign({}, userContext, {
			experiments
		});
		model.set('userContext', userContext);

		return userContext;
	},

	updateCampaign(campaign) {
		if (!this.loggedIn()) {
			return null;
		}

		let userContext = model.get('userContext');
		userContext = _.assign({}, userContext, {
			campaign
		});
		model.set('userContext', userContext);

		return userContext;
	}
};
